import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import Gleap from 'gleap';

import { getAppAuth, IMPERSONATION_FLAG } from '../../api/axios.instance';
import { getGetAccountQueryOptions } from '../../api/generated/api';
import { identifyCrazyEggUser } from '../../modules/crazy-egg';
import { identifyGoogleAnalyticsUser } from '../../modules/google-analytics';
import { identifyPayticsUser } from '../../modules/paytics';
import { identifySentryUser } from '../../modules/sentry';
import { hideHtmlLoader, setHtmlLoaderText } from '../../ui/utils/html';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    const user = getAppAuth().currentUser;
    const redirectTo = location.href;

    if (!user) {
      throw redirect({ to: '/auth/signin', search: { redirectTo } });
    }

    if (sessionStorage.getItem(IMPERSONATION_FLAG) !== '1') {
      const hasTrustedOAuth = user.providerData.some(
        (p) => p.providerId === 'google.com' || p.providerId === 'microsoft.com',
      );

      const hasPassword = user.providerData.some((p) => p.providerId === 'password');
      const verified = hasTrustedOAuth || (hasPassword && user.emailVerified);
      if (!verified) {
        throw redirect({ to: '/auth/action', search: { mode: 'verifyEmail' } });
      }
    }

    setHtmlLoaderText('Loading account information ...');
    const { data: account } = await context.queryClient.ensureQueryData(
      getGetAccountQueryOptions(),
    );

    if (!account) {
      throw redirect({ to: '/auth/signin', search: { redirectTo } });
    }

    hideHtmlLoader();

    Gleap.identify(account.id, {
      email: account.email,
      name: `${account.firstName} ${account.lastName}`,
    });

    identifySentryUser(account);

    if (import.meta.env.VITE_ENV === 'production') {
      identifyCrazyEggUser(account.email);
    }

    // An impersonating superadmin's browser must not become the customer's user in GA4.
    if (sessionStorage.getItem(IMPERSONATION_FLAG) !== '1') {
      identifyGoogleAnalyticsUser(account.id);
      identifyPayticsUser(account.id);
    }

    return { account };
  },
  component: () => <Outlet />,
});
